import { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, CardHeader, Typography, Button, Grid,
  FormControl, InputLabel, Select, MenuItem, TextField, Chip,
  Avatar, Divider, Alert, Tooltip, IconButton, ToggleButtonGroup,
  ToggleButton, LinearProgress,
} from '@mui/material';
import toast from 'react-hot-toast';
import { attendanceApi } from '@/api/attendance.api';
import { studentsApi } from '@/api/students.api';
import useSchoolStore from '@/store/schoolStore';
import { PageHeader } from '@/components/navigation/PageHeader';
import { usePageMeta } from '@/hooks/usePageMeta';
import {
  UserCheck, Clock, AlertCircle, Save, RefreshCw,
  CalendarCheck2, Users, CheckCircle2, XCircle, ChevronDown,
} from 'lucide-react';
import { AcademicSessionGuard } from '@/components/common/AcademicSessionGuard';

// ── Status colours & labels ────────────────────────────────────
const STATUS_CONFIG = {
  PRESENT:  { label: 'Present',   color: 'success', icon: CheckCircle2 },
  ABSENT:   { label: 'Absent',    color: 'error',   icon: XCircle },
  LATE:     { label: 'Late',      color: 'warning', icon: Clock },
  HALF_DAY: { label: 'Half Day',  color: 'info',    icon: AlertCircle },
};

// ── Quick-mark strip for a single student ─────────────────────
function StudentRow({ student, onChange, disabled }) {
  const { status, remarks } = student;
  const [showRemarks, setShowRemarks] = useState(false);

  const fullName = [student.first_name, student.middle_name, student.last_name]
    .filter(Boolean).join(' ');
  const initials = `${student.first_name?.[0] ?? ''}${student.last_name?.[0] ?? ''}`.toUpperCase();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: status === 'ABSENT' ? 'error.light'
          : status === 'LATE' ? 'warning.light'
          : status === 'HALF_DAY' ? 'info.light'
          : status === 'PRESENT' ? 'success.light'
          : 'divider',
        bgcolor: status === 'ABSENT' ? 'error.50'
          : status === 'LATE' ? 'warning.50'
          : status === 'HALF_DAY' ? 'info.50'
          : status === 'PRESENT' ? 'success.50'
          : 'background.paper',
        transition: 'all 0.2s ease',
        opacity: disabled ? 0.65 : 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {/* Avatar + name */}
        <Avatar sx={{ width: 38, height: 38, bgcolor: 'primary.main', fontSize: 14, fontWeight: 700 }}>
          {initials}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 120 }}>
          <Typography variant="body2" fontWeight={700}>{fullName}</Typography>
          <Typography variant="caption" color="text.secondary">
            Roll #{student.roll_number} · {student.admission_no}
          </Typography>
        </Box>

        {/* Status toggle */}
        <ToggleButtonGroup
          value={status}
          exclusive
          size="small"
          disabled={disabled}
          onChange={(_, v) => { if (v) onChange('status', v); }}
          sx={{ flexShrink: 0 }}
        >
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
            <ToggleButton
              key={key}
              value={key}
              color={cfg.color}
              sx={{
                px: 1.5,
                py: 0.5,
                fontSize: '0.72rem',
                fontWeight: 700,
                '&.Mui-selected': {
                  bgcolor: `${cfg.color}.main`,
                  color: '#fff',
                  '&:hover': { bgcolor: `${cfg.color}.dark` },
                },
              }}
            >
              {cfg.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {/* Remarks toggle */}
        <Tooltip title="Add remarks">
          <IconButton size="small" disabled={disabled} onClick={() => setShowRemarks((p) => !p)}>
            <ChevronDown size={16} style={{ transform: showRemarks ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Remarks */}
      {showRemarks && (
        <TextField
          size="small"
          placeholder="Remarks (optional)…"
          value={remarks ?? ''}
          disabled={disabled}
          onChange={(e) => onChange('remarks', e.target.value)}
          fullWidth
          sx={{ mt: 0.5 }}
        />
      )}
    </Box>
  );
}

const getTodayStr = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// ── Main Page ─────────────────────────────────────────────────
export function StudentAttendanceDailyPage() {
  usePageMeta('Student Attendance | Mark Attendance', [
    { label: 'Attendance', path: '/attendance' },
    { label: 'Student Attendance' },
  ]);

  const { currentSchool } = useSchoolStore();
  const todayStr = getTodayStr();

  // Filter state
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [attendanceDate, setAttendanceDate] = useState(() => todayStr);
  const [periodNo] = useState(0); // DAILY mode always 0

  // Attendance state
  const [students, setStudents] = useState([]);       // [{...student, status, remarks}]
  const [sessionId, setSessionId] = useState(null);   // existing attendance_session_id
  const [isLocked, setIsLocked] = useState(false);
  const [attendanceExists, setAttendanceExists] = useState(false);
  const [editable, setEditable] = useState(true);

  // UI state
  const [fetching, setFetching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Load classes + sections once
  useEffect(() => {
    studentsApi.getClassesSections().then((res) => {
      const list = res?.data || res?.classes || res || [];
      setClasses(Array.isArray(list) ? list : []);
    }).catch(console.error);
  }, []);

  // Update sections when class changes
  useEffect(() => {
    setSectionId('');
    if (!classId) { setSections([]); return; }
    const cls = classes.find((c) => c.id === classId);
    setSections(cls?.sections || []);
  }, [classId, classes]);

  // Fetch students for attendance when class + section + date chosen
  const fetchStudents = useCallback(async () => {
    if (!currentSchool?.id || !classId || !sectionId || !attendanceDate) return;
    try {
      setFetching(true);
      setLoaded(false);
      const res = await attendanceApi.getStudentsForAttendance(currentSchool.id, {
        classId,
        sectionId,
        date: attendanceDate.replace(/-/g, '/'),
        periodNo,
      });

      if (res?.success) {
        setAttendanceExists(res.attendance_exists ?? false);
        setEditable(res.editable ?? true);
        setSessionId(res.session_id ?? null);
        // Map students, preserve existing status from API (null if no attendance yet)
        const mapped = (res.students || []).map((s) => ({
          ...s,
          status: s.status || null,
          remarks: s.remarks || '',
        }));
        setStudents(mapped);
        setIsLocked(false);
        setLoaded(true);
      }
    } catch (err) {
      toast.error('Failed to load students.');
      console.error(err);
    } finally {
      setFetching(false);
    }
  }, [currentSchool?.id, classId, sectionId, attendanceDate, periodNo]);

  // Auto-fetch students whenever classId, sectionId, or attendanceDate changes and all 3 are set
  useEffect(() => {
    if (currentSchool?.id && classId && sectionId && attendanceDate) {
      fetchStudents();
    } else {
      setLoaded(false);
      setStudents([]);
    }
  }, [currentSchool?.id, classId, sectionId, attendanceDate, fetchStudents]);

  const handleDateChange = (val) => {
    if (val > todayStr) {
      toast.error('Future dates cannot be selected for attendance.');
      setAttendanceDate(todayStr);
      return;
    }
    setAttendanceDate(val);
  };

  // Update single student field
  const handleStudentChange = (enrollmentId, field, value) => {
    setStudents((prev) =>
      prev.map((s) =>
        s.student_enrollment_id === enrollmentId ? { ...s, [field]: value } : s
      )
    );
  };

  // Mark all present / all absent
  const markAll = (status) => {
    setStudents((prev) => prev.map((s) => ({ ...s, status })));
  };

  // Summary counts
  const counts = students.reduce((acc, s) => {
    if (s.status) {
      acc[s.status] = (acc[s.status] || 0) + 1;
    } else {
      acc.UNMARKED = (acc.UNMARKED || 0) + 1;
    }
    return acc;
  }, {});

  // Submit
  const handleSubmit = async () => {
    if (!classId || !sectionId || students.length === 0) return;
    const unmarkedCount = students.filter((s) => !s.status).length;
    if (unmarkedCount > 0) {
      toast.error(`Please select attendance status for all students (${unmarkedCount} unmarked).`);
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        class_id: classId,
        section_id: sectionId,
        attendance_date: attendanceDate,
        period_no: periodNo,
        attendance: students.map((s) => ({
          student_enrollment_id: s.student_enrollment_id,
          status: s.status,
          remarks: s.remarks || '',
        })),
      };

      let res;
      if (attendanceExists && sessionId) {
        // Update existing
        res = await attendanceApi.updateStudentAttendance(currentSchool.id, sessionId, payload);
      } else {
        // Submit new
        res = await attendanceApi.submitStudentAttendance(currentSchool.id, payload);
      }

      if (res?.success) {
        toast.success(res.message || 'Attendance saved successfully.');
        // Update session state from response
        if (res.attendance_session) {
          setSessionId(res.attendance_session.id);
          setIsLocked(res.attendance_session.is_locked ?? false);
          setAttendanceExists(true);
        }
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to save attendance.';
      if (msg.toLowerCase().includes('locked')) {
        setIsLocked(true);
        toast.error('Attendance is locked and cannot be edited.');
      } else {
        toast.error(msg);
      }
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = loaded && students.length > 0 && !isLocked && editable && !fetching;

  return (
    <AcademicSessionGuard>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <PageHeader
        title="Student Attendance"
        subtitle="Select class and section to mark daily attendance"
        action={
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {loaded && (
              <Button
                variant="outlined"
                startIcon={<RefreshCw size={16} />}
                onClick={fetchStudents}
                disabled={fetching || submitting}
              >
                Reload
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<Save size={16} />}
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
            >
              {submitting ? 'Saving…' : attendanceExists ? 'Update Attendance' : 'Submit Attendance'}
            </Button>
          </Box>
        }
      />

      {/* ── Filters ───────────────────────────────────────────── */}
      <Card>
        <CardContent>
          <Grid container spacing={2} sx={{ alignItems: 'center' }}>
            <Grid size={{ xs: 12, sm: 4, md: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Class</InputLabel>
                <Select value={classId} label="Class" onChange={(e) => setClassId(e.target.value)}>
                  <MenuItem value=""><em>Select Class</em></MenuItem>
                  {classes.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.class_name || c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4, md: 4 }}>
              <FormControl fullWidth size="small" disabled={!classId}>
                <InputLabel>Section</InputLabel>
                <Select value={sectionId} label="Section" onChange={(e) => setSectionId(e.target.value)}>
                  <MenuItem value=""><em>Select Section</em></MenuItem>
                  {sections.map((s) => (
                    <MenuItem key={s.id} value={s.id}>{s.section_name || s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Date"
                value={attendanceDate}
                onChange={(e) => handleDateChange(e.target.value)}
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: { max: todayStr },
                }}
                inputProps={{ max: todayStr }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {fetching && <LinearProgress sx={{ borderRadius: 1 }} />}

      {/* ── Alerts ─────────────────────────────────────────────── */}
      {isLocked && (
        <Alert severity="error" icon={<AlertCircle size={18} />}>
          <strong>Attendance Locked</strong> — This session has been submitted and locked. Contact an administrator to edit.
        </Alert>
      )}
      {loaded && !editable && !isLocked && (
        <Alert severity="warning">
          Editing is not allowed for this attendance record (edit window has expired).
        </Alert>
      )}
      {loaded && attendanceExists && editable && !isLocked && (
        <Alert severity="info">
          Attendance already exists for this class/section/date. You are editing an existing record.
        </Alert>
      )}

      {/* ── Student List ─────────────────────────────────────── */}
      {loaded && students.length > 0 && (
        <Grid container spacing={3}>
          {/* Left: student rows */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Card>
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Users size={20} />
                    <Typography variant="h6" fontWeight={600}>
                      Students ({students.length})
                    </Typography>
                  </Box>
                }
                action={
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" color="success" variant="outlined" onClick={() => markAll('PRESENT')}>
                      All Present
                    </Button>
                    <Button size="small" color="error" variant="outlined" onClick={() => markAll('ABSENT')}>
                      All Absent
                    </Button>
                  </Box>
                }
              />
              <Divider />
              <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {students.map((student) => (
                  <StudentRow
                    key={student.student_enrollment_id}
                    student={student}
                    disabled={isLocked || !editable}
                    onChange={(field, value) =>
                      handleStudentChange(student.student_enrollment_id, field, value)
                    }
                  />
                ))}
              </CardContent>
            </Card>
          </Grid>

          {/* Right: summary */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ position: 'sticky', top: 80 }}>
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CalendarCheck2 size={20} />
                    <Typography variant="h6" fontWeight={600}>Summary</Typography>
                  </Box>
                }
              />
              <Divider />
              <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Counts */}
                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
                  const Icon = cfg.icon;
                  const count = counts[key] || 0;
                  return (
                    <Box
                      key={key}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: `${cfg.color}.50`,
                        border: '1px solid',
                        borderColor: `${cfg.color}.light`,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Icon size={16} color={`var(--mui-palette-${cfg.color}-main)`} />
                        <Typography variant="body2" fontWeight={600}>{cfg.label}</Typography>
                      </Box>
                      <Chip label={count} size="small" color={cfg.color} />
                    </Box>
                  );
                })}

                <Divider />

                {/* Attendance % */}
                {students.length > 0 && (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">Attendance Rate</Typography>
                      <Typography variant="caption" fontWeight={700}>
                        {counts.UNMARKED === students.length ? '—' : `${Math.round(((counts.PRESENT || 0) / students.length) * 100)}%`}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={counts.UNMARKED === students.length ? 0 : ((counts.PRESENT || 0) / students.length) * 100}
                      color="success"
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                )}

                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<UserCheck size={18} />}
                  onClick={handleSubmit}
                  disabled={!canSubmit || submitting}
                  size="large"
                >
                  {submitting ? 'Saving…' : attendanceExists ? 'Update' : 'Submit Attendance'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Empty state */}
      {!fetching && !loaded && (
        <Card>
          <CardContent sx={{ py: 8, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <CalendarCheck2 size={48} style={{ opacity: 0.2, marginBottom: 16 }} />
            <Typography variant="h6" color="text.secondary">
              Select class, section, and date to view students
            </Typography>
          </CardContent>
        </Card>
      )}

      {loaded && students.length === 0 && (
        <Card>
          <CardContent sx={{ py: 6, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Users size={40} style={{ opacity: 0.2, marginBottom: 12 }} />
            <Typography variant="h6" color="text.secondary">No students found</Typography>
            <Typography variant="body2" color="text.secondary">
              No enrolled students in this class/section for the selected session.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
    </AcademicSessionGuard>
  );
}
